import { useCallback, useEffect, useState } from 'react';

import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import {
  ShortCodePollingStatus,
  useShortCodePollStatus,
} from 'state/loyalty/in-restaurant-redemption/hooks/use-short-code-poll-status';
import { useInRestaurantRedemptionContext } from 'state/loyalty/in-restaurant-redemption/in-restaurant-redemption-context';
import { ShortCodeState } from 'state/loyalty/in-restaurant-redemption/types';
import { useLoyaltyContext } from 'state/loyalty/loyalty-context';

import { usePollingInterval } from '../hooks/use-polling-interval';
import ShortCodeConfirmationModal from '../modals/short-code-confirmation-modal';

export function ShortCodeOfferStatusPoller() {
  const pollInterval = usePollingInterval();
  const { loyaltyUser } = useLoyaltyUser();
  const loyaltyId = loyaltyUser?.id;
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const { refetchLoyaltyUser } = useLoyaltyContext();

  const {
    shortCode,
    shortCodeLoading,
    shortCodeState,
    shouldResetPollingResultStatus,
    pollingShortCodeStatus,
    setShortCodeClaimed,
    setShortCodeExpired,
    isQuickPayEnabled,
    setShouldResetPollingResultStatus,
    resetInRestaurantRedemption,
    setPollingShortCodeStatus,
  } = useInRestaurantRedemptionContext();

  const skipPolling: boolean =
    !pollInterval || shortCodeLoading || shortCodeState !== ShortCodeState.Pending;

  const {
    pollingShortCodeStatus: shortCodeOfferStatus,
    resetPollingStatus,
  } = useShortCodePollStatus({
    loyaltyId,
    skipPolling,
    shortCode,
    pollInterval,
  });

  useEffect(() => {
    if (pollingShortCodeStatus !== shortCodeOfferStatus) {
      setPollingShortCodeStatus(shortCodeOfferStatus);
    }
  }, [pollingShortCodeStatus, setPollingShortCodeStatus, shortCodeOfferStatus]);

  useEffect(() => {
    if (shortCodeOfferStatus === ShortCodePollingStatus.Confirmed) {
      setShortCodeClaimed();
    } else if (shortCodeOfferStatus === ShortCodePollingStatus.NotFound) {
      // expiring the code if it's not found by the polling request
      setShortCodeExpired();
    }
  }, [setShortCodeClaimed, setShortCodeExpired, shortCodeOfferStatus]);

  useEffect(() => {
    if (shortCodeState === ShortCodeState.Claimed && !isQuickPayEnabled) {
      setShowConfirmationModal(true);
    }
  }, [shortCodeState, isQuickPayEnabled]);

  useEffect(() => {
    if (shouldResetPollingResultStatus) {
      resetPollingStatus();
      setShouldResetPollingResultStatus(false);
    }
  }, [resetPollingStatus, setShouldResetPollingResultStatus, shouldResetPollingResultStatus]);

  const onConfirmationModalAction = useCallback(() => {
    resetInRestaurantRedemption();
    // re-fetching user points
    refetchLoyaltyUser();
    setShowConfirmationModal(false);
  }, [refetchLoyaltyUser, resetInRestaurantRedemption]);

  return (
    <>
      {showConfirmationModal && (
        <ShortCodeConfirmationModal
          onAction={onConfirmationModalAction}
          onDismiss={onConfirmationModalAction}
        />
      )}
    </>
  );
}
